import hardtack from 'hardtack';

type Listener = { lastUpdate: number; callbacks: Array<(value: unknown) => void> };
type Listeners = Record<string, Listener>;
type EventInfo = {
  lastUpdate: number;
};

type Event = { lastUpdate: number; data: unknown };
type Events = Record<string, Event>;

function setCookie(value: Events, global: boolean): void {
  const options = global ? { path: '/' } : {};
  const cookieName = global ? 'CrosstabGlobal' : 'Crosstab';

  hardtack.set(cookieName, JSON.stringify(value), options);
}

function getCookie(global: boolean): Events | undefined {
  const cookieName = global ? 'CrosstabGlobal' : 'Crosstab';

  return JSON.parse(hardtack.get(cookieName) || '{}');
}

export class CrosstabCtor {
  private listeners: Listeners = {};

  private globalListeners: Listeners = {};

  private triggerListenersTimerId: ReturnType<typeof setInterval> | undefined;

  private triggerGlobalListenersTimerId: ReturnType<typeof setInterval> | undefined;

  /**
   * Start listening to a new signal.
   *
   * @param eventId Name of event to listen for.
   * @param fn Callback function.
   * @param global Whether to listens to global events (not ones only set for current url and its sub urls).
   */
  public listen(eventId: string, fn: (value: unknown) => void, global: boolean): void {
    const eventInfo: EventInfo | undefined = (getCookie(global) || {})[eventId];

    const listeners = global ? this.globalListeners : this.listeners;

    listeners[eventId] = listeners[eventId] || {
      lastUpdate: eventInfo && eventInfo.lastUpdate,
      callbacks: [],
    };

    listeners[eventId].callbacks.push(fn);

    let timerId = global ? this.triggerGlobalListenersTimerId : this.triggerListenersTimerId;
    // Start checking for events once the first listener has been registered.
    if (typeof timerId === 'undefined') {
      this.triggerListeners(global);
      timerId = setInterval(() => {
        this.triggerListeners(global);
      }, 1000);

      if (global) {
        this.triggerGlobalListenersTimerId = timerId;
      } else {
        this.triggerListenersTimerId = timerId;
      }
    }
  }

  /**
   * Broadcast a new signal to all listeners.
   *
   * @param eventId Id of event.
   * @param data [optional] Data to send with the event.
   * @param global Whether to signal a global events (not ones only set for current url and its sub urls).
   */
  // eslint-disable-next-line class-methods-use-this
  public signal(eventId: string, data: unknown, global: boolean): void {
    const events = getCookie(global) || {};

    events[eventId] = {
      lastUpdate: new Date().getTime(),
      data,
    };

    setCookie(events, global);
  }

  public triggerListeners(global: boolean): void {
    const events = getCookie(global) || {};
    const listeners = global ? this.globalListeners : this.listeners;

    // eslint-disable-next-line no-restricted-syntax
    for (const eventId of Object.keys(listeners)) {
      const eventInfo = events[eventId];
      const listenerInfo = listeners[eventId];

      if (eventInfo && eventInfo.lastUpdate !== listenerInfo.lastUpdate) {
        listenerInfo.lastUpdate = eventInfo.lastUpdate;
        listenerInfo.callbacks.forEach((callback) => {
          callback(eventInfo.data);
        });
      }
    }
  }
}

export const Crosstab = new CrosstabCtor();
